import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Card,
  Stack,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useForm, useFormContext } from 'react-hook-form';
import timezones from 'timezones-list';
import FormProvider, {
  RHFTextField,
  RHFSelect,
} from '../components/hook-form';
import WorkingHoursWithCopy from '../components/working-hours/WorkingHoursWithCopy';
import CreateServiceStep from '../components/onboarding/CreateServiceStep';
import ConnectWebflowStep from '../components/onboarding/ConnectWebflowStep';
import { getValidConfig } from '../utils/schedule';
import axios from '../utils/Axios';
import { useAuthContext } from '../auth/useAuthContext';
import Logo from '../components/logo';

const StyledContent = styled('div')(({ theme }) => ({
  margin: 'auto',
  maxWidth: 700,
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(4, 0, 7.5, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 0, 6, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 0, 5, 0),
  },
}));

const stepLabels = ['Welcome', 'Configure Availability', 'Create Your Service', 'Connect Webflow'];

const ROLE_OPTIONS = [
  'Freelancer',
  'Business Owner',
  'Agency',
  'Developer / Web Designer',
  'Other'
];

const BUSINESS_TYPE_OPTIONS = [
  'Personal Coaching',
  'Therapy / Mental Health',
  'Beauty & Wellness',
  'Fitness / Yoga / Classes',
  'Photography / Videography',
  'Legal / Financial Consulting',
  'Real Estate / Property Booking',
  'Events / Rentals / Venues',
  'Clinics / Healthcare',
  'Automotive Services',
  'Other'
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setGlobalUser } = useAuthContext();
  const [shouldLoadPage, setShouldLoadPage] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [initialStepCalculated, setInitialStepCalculated] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [isLoadingStep1, setIsLoadingStep1] = useState(false);
  const [isLoadingStep2, setIsLoadingStep2] = useState(false);
  const [isLoadingStep3, setIsLoadingStep3] = useState(false);
  const [userServices, setUserServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const needsSubscription = !user?.subscription?.subscriptionId;
  
  // Helper function to map backend role enum to display value
  const mapRoleToDisplay = (role) => {
    switch (role) {
      case 'FREELANCER':
        return 'Freelancer';
      case 'AGENCY':
        return 'Agency';
      case 'BUSINESS_OWNER':
        return 'Business Owner';
      case 'DEVELOPER':
        return 'Developer / Web Designer';
      default:
        return role;
    }
  };
  
  // Helper function to determine if role/businessType is custom
  const isCustomRole = (role) => {
    // Check if it's one of the backend enum values or display values
    const backendEnums = ['FREELANCER', 'AGENCY', 'BUSINESS_OWNER', 'DEVELOPER'];
    const displayRole = mapRoleToDisplay(role);
    
    if (backendEnums.includes(role) || ROLE_OPTIONS.includes(displayRole)) {
      return false;
    }
    return role !== '' && role !== null;
  };
  
  const isCustomBusinessType = (type) => {
    return !BUSINESS_TYPE_OPTIONS.includes(type) && type !== '' && type !== null;
  };
  
  // Map backend role to display value for form
  const userRole = mapRoleToDisplay(user?.companyRole || '');
  
  const defaultValues = {
    role: isCustomRole(user?.companyRole) ? 'Other' : userRole,
    roleOther: isCustomRole(user?.companyRole) ? user?.companyRole : '',
    businessType: isCustomBusinessType(user?.businessType) ? 'Other' : user?.businessType || '',
    businessTypeOther: isCustomBusinessType(user?.businessType) ? user?.businessType : '',
    serviceName: '',
    serviceDuration: 30,
    serviceDurationType: 'minutes',
    config: {
      timezone: user?.config?.timezone || 'Europe/London',
      availability: user?.config?.availability || []
    }
  };

  useEffect(() => {
    if (needsSubscription) {
      navigate('/dashboard/app', { replace: true });
      return;
    }

    // Redirect if onboarding is already completed
    if (user?.onboardingCompleted === true) {
      navigate('/dashboard/app', { replace: true });
      return;
    }

    const isNotAllowedtoOnboard =
      user?.companyRole ||
      (user?.subscription && !user?.subscription.subscriptionId);
    if (false) {
      navigate('/dashboard', { replace: true });
    } else {
      setShouldLoadPage(true);
      // Initialize availability with default 7-day structure
      const defaultAvailability = Array.from({ length: 7 }, (_, index) => ({
        day: index,
        startDate: 'off',
        endDate: 'off',
        breaks: []
      }));
      
      if (!user?.config?.availability?.length) {
        setAvailability(defaultAvailability);
      } else {
        // Ensure we have all 7 days, even if user has partial data
        const existingAvailability = user.config.availability;
        const completeAvailability = Array.from({ length: 7 }, (_, index) => {
          const existingDay = existingAvailability.find(day => day.day === index);
          return existingDay || {
            day: index,
            startDate: 'off',
            endDate: 'off',
            breaks: []
          };
        });
        setAvailability(completeAvailability);
      }
    }
  }, [user, navigate]);

  // Helper function to determine initial step based on completed data
  const determineInitialStep = (userData, services) => {
    // Step 4 (Connect Webflow): Role + availability + services exist, but need to check for bookings
    if (userData?.companyRole && userData?.config?.availability?.length > 0 && services?.length > 0) {
      return 3;
    }
    
    // Step 3 (Create Service): Role + availability exist, but no services
    if (userData?.companyRole && userData?.config?.availability?.length > 0) {
      return 2;
    }
    
    // Step 2 (Configure Availability): Role exists but no availability configured  
    if (userData?.companyRole) {
      return 1;
    }
    
    // Step 1 (Welcome): Nothing completed yet
    return 0;
  };

  // Fetch fresh user data and services on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingServices(true);
        
        // Fetch latest user data to ensure we have the most current information
        const userResponse = await axios.get('/user');
        if (userResponse.data.user) {
          setGlobalUser(userResponse.data.user);
        }
        
        // Fetch services for Step 3
        const servicesResponse = await axios.get('/services');
        setUserServices(servicesResponse.data.services || []);
        
        // Determine the correct initial step based on completed data
        if (!initialStepCalculated) {
          const initialStep = determineInitialStep(userResponse.data.user, servicesResponse.data.services || []);
          if (initialStep !== null) {
            setActiveStep(initialStep);
          }
          setInitialStepCalculated(true);
        }
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
        setInitialStepCalculated(true); // Still mark as calculated to prevent infinite loading
      } finally {
        setIsLoadingServices(false);
      }
    };
    
    // Only fetch if we have a user (authenticated)
    if (user && !initialStepCalculated) {
      fetchData();
    }
  }, [user, initialStepCalculated, navigate]);

  const methods = useForm({ defaultValues });
  const { handleSubmit } = methods;

  const onSubmit = async () => {
    try {
      console.log('[onboarding] submit start');

      // Trigger backend auto-check by sending minimal valid config
      // This will cause backend to evaluate all onboarding criteria and update onboardingCompleted field
      const minimalPayload = {
        config: {
          timezone: user?.config?.timezone || 'Europe/London',
          bookingStatus: user?.config?.bookingStatus || 'PENDING',
          beforeBuffer: user?.config?.beforeBuffer || 0,
          afterBuffer: user?.config?.afterBuffer || 0,
          availability: user?.config?.availability || []
        }
      };

      console.log('[onboarding] triggering backend auto-check with PUT /user');
      const response = await axios.put('/user', minimalPayload);
      console.log('[onboarding] user updated, onboardingCompleted:', response.data?.user?.onboardingCompleted);

      // Update global user state with the response that includes onboardingCompleted field
      if (response.data.user) {
        setGlobalUser(response.data.user);
      }

      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('[onboarding] Error completing onboarding:', error);
      // Don't navigate on error - user should see what went wrong
      alert('Unable to complete onboarding. Please ensure all steps are finished and try again.');
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Step-specific data submission handlers
  const handleStep1Next = async () => {
    const formData = methods.getValues();
    
    // Clear any previous errors
    methods.clearErrors();
    
    // Validation
    let hasErrors = false;
    
    if (!formData.role) {
      methods.setError('role', { message: 'Please select your role' });
      hasErrors = true;
    }
    
    if (!formData.businessType) {
      methods.setError('businessType', { message: 'Please select your business type' });
      hasErrors = true;
    }
    
    // If role is "Other", validate the custom input
    if (formData.role === 'Other' && (!formData.roleOther || formData.roleOther.trim() === '')) {
      methods.setError('roleOther', { message: 'Please specify your role' });
      hasErrors = true;
    }
    
    // If business type is "Other", validate the custom input
    if (formData.businessType === 'Other' && (!formData.businessTypeOther || formData.businessTypeOther.trim() === '')) {
      methods.setError('businessTypeOther', { message: 'Please specify your business type' });
      hasErrors = true;
    }
    
    // Stop if validation fails
    if (hasErrors) {
      return;
    }
    
    setIsLoadingStep1(true);
    try {
      const finalRole = formData.role === 'Other' ? (formData.roleOther || formData.role) : formData.role;
      const finalBusinessType = formData.businessType === 'Other' ? (formData.businessTypeOther || formData.businessType) : formData.businessType;
      
      // Store the actual role value for custom roles
      let companyRole = finalRole;
      
      // Only map standard roles to backend enum values
      if (formData.role !== 'Other') {
        switch (finalRole) {
          case 'Business Owner':
            companyRole = 'BUSINESS_OWNER';
            break;
          case 'Agency':
            companyRole = 'AGENCY';
            break;
          case 'Developer / Web Designer':
            companyRole = 'DEVELOPER';
            break;
          case 'Freelancer':
            companyRole = 'FREELANCER';
            break;
        }
      }
      
      const payload = {
        companyRole,
        businessType: finalBusinessType,
      };
      
      const response = await axios.put('/user', payload);
      // Update global user state with the response
      if (response.data.user) {
        setGlobalUser(response.data.user);
      }
      setActiveStep(1);
    } catch (error) {
      console.error('Error saving user info:', error);
      // Still proceed to next step even if save fails
      setActiveStep(1);
    } finally {
      setIsLoadingStep1(false);
    }
  };

  const handleStep2Next = async () => {
    setIsLoadingStep2(true);
    try {
      const formData = methods.getValues();
      const filteredAvailability = availability.filter(
        (day) => day.startDate !== 'off' && day.endDate !== 'off'
      );
      
      const payload = {
        config: {
          ...formData.config,
          availability: filteredAvailability,
          bookingStatus: 'PENDING',
          beforeBuffer: 0,
          afterBuffer: 0,
          holidays: [],
          timezone: formData.config.timezone || 'Europe/London'
        }
      };
      
      const response = await axios.put('/user', payload);
      // Update global user state with the response
      if (response.data.user) {
        setGlobalUser(response.data.user);
      }
      setActiveStep(2);
    } catch (error) {
      console.error('Error saving availability:', error);
      // Still proceed to next step even if save fails
      setActiveStep(2);
    } finally {
      setIsLoadingStep2(false);
    }
  };

  const handleStep3Next = async (showCreateForm = false) => {
    // If user already has services and is NOT creating a new one, just proceed to next step
    if (userServices.length > 0 && !showCreateForm) {
      setActiveStep(3);
      return;
    }
    
    const formData = methods.getValues();
    
    // Clear any previous errors
    methods.clearErrors();
    
    // Validation
    let hasErrors = false;
    
    // Validate service name
    if (!formData.serviceName || formData.serviceName.trim() === '') {
      methods.setError('serviceName', { message: 'Please enter a service name' });
      hasErrors = true;
    } else if (formData.serviceName.trim().length < 2) {
      methods.setError('serviceName', { message: 'Service name must be at least 2 characters' });
      hasErrors = true;
    }
    
    // Validate duration - convert to minutes and check against 24 hours (1440 minutes)
    const duration = parseFloat(formData.serviceDuration) || 0;
    const durationType = formData.serviceDurationType || 'minutes';
    const durationInMinutes = durationType === 'hours' ? duration * 60 : duration;
    
    if (duration < 1) {
      methods.setError('serviceDuration', { message: 'Duration must be at least 1' });
      hasErrors = true;
    } else if (durationInMinutes > 1440) {
      methods.setError('serviceDuration', { message: 'Duration cannot exceed 24 hours' });
      hasErrors = true;
    }
    
    // Stop if validation fails
    if (hasErrors) {
      return;
    }
    
    setIsLoadingStep3(true);
    try {
      
      // Calculate duration in minutes from form inputs
      const durationInMinutes = parseFloat(formData.serviceDuration) * (formData.serviceDurationType === 'hours' ? 60 : 1);
      
      // First, ensure "Uncategorized" category exists
      let uncategorizedCategoryId = null;
      try {
        const categoriesResponse = await axios.get('/categories');
        let categories = categoriesResponse.data.categories;
        
        // Find existing "Uncategorized" category
        const uncategorizedCategory = categories.find(cat => 
          cat.name.toLowerCase() === 'uncategorized'
        );
        
        if (uncategorizedCategory) {
          uncategorizedCategoryId = uncategorizedCategory._id;
        } else {
          // Create "Uncategorized" category if it doesn't exist
          const uncategorizedResponse = await axios.post('/categories', {
            name: 'Uncategorized',
          });
          uncategorizedCategoryId = uncategorizedResponse.data.category._id;
        }
      } catch (categoryError) {
        console.error('Error handling category:', categoryError);
        // Continue with null category if category handling fails
      }
      
      // Create service via API with all required parameters matching working payload
      const serviceData = {
        name: formData.serviceName,
        previewImage: "",
        duration: 30, // Slot increment - standard default
        durationType: formData.serviceDurationType === 'hours' ? 'Hours' : 'Minutes',
        durationTypeValue: formData.serviceDurationType === 'hours' ? formData.serviceDuration : durationInMinutes,
        category: uncategorizedCategoryId, // Use actual category ID instead of null
        config: {
          beforeBuffer: 0,
          afterBuffer: 0,
          availability: []
        },
        hidden: false,
        description: "",
        bufferDays: "0",
        capacity: 1,
        price: 0
      };
      
      const response = await axios.post('/services', serviceData);
      // Update the local services list
      if (response.data.service) {
        setUserServices([...userServices, response.data.service]);
      }
      
      // Refresh services list
      const servicesResponse = await axios.get('/services');
      setUserServices(servicesResponse.data.services || []);
      
      setActiveStep(3);
    } catch (error) {
      console.error('Error creating service:', error);
      // Still proceed to next step even if save fails
      setActiveStep(3);
    } finally {
      setIsLoadingStep3(false);
    }
  };

  // Don't render anything if onboarding is already complete (prevents flash before redirect)
  if (user?.onboardingCompleted === true) {
    return null;
  }

  return (
    needsSubscription ? null : (
      <>
      <Helmet>
        <title> Onboarding | {localStorage.getItem("companyName") ?? "FlowBookings"} </title>
      </Helmet>
      {shouldLoadPage && initialStepCalculated ? (
        <Container maxWidth="xl">
          <StyledContent>
            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 7,
                '& .MuiStepLabel-root': {
                  flexFlow: 'column',
                  rowGap: '8px',
                  marginBottom: '-26px',
                },
              }}
            >
              {stepLabels.map((label, index) => (
                <Step key={index}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '12px',
                        fontWeight: index === activeStep ? 600 : 400,
                        color: index === activeStep ? 'primary.main' : index < activeStep ? 'text.primary' : 'grey.500',
                        textAlign: 'center',
                        [theme => theme.breakpoints.down('sm')]: {
                          fontSize: '10px',
                        },
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            
            <Card sx={{ p: 4 }}>
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {activeStep === 0 && (
                  <WelcomeStep onNext={handleStep1Next} isLoading={isLoadingStep1} />
                )}
                {activeStep === 1 && (
                  <AvailabilityStep 
                    availability={availability}
                    setAvailability={setAvailability}
                    onNext={handleStep2Next}
                    onBack={handleBack}
                    isLoading={isLoadingStep2}
                  />
                )}
                {activeStep === 2 && (
                  <CreateServiceStep 
                    onNext={handleStep3Next} 
                    onBack={handleBack} 
                    isLoading={isLoadingStep3}
                    userServices={userServices}
                    setUserServices={setUserServices}
                    isLoadingServices={isLoadingServices}
                  />
                )}
                {activeStep === 3 && (
                  <ConnectWebflowStep onBack={handleBack} onComplete={handleSubmit(onSubmit)} />
                )}
              </FormProvider>
            </Card>
          </StyledContent>
        </Container>
      ) : null}
      </>
    )
  );
}

function WelcomeStep({ onNext, isLoading }) {
  const { watch } = useFormContext();
  const selectedRole = watch('role');
  const selectedBusinessType = watch('businessType');
  
  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, textWrap: 'balance' }}>
        You're 2 minutes away from collecting your first booking!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 6 }}>
        You can change service details and add more services later on.
      </Typography>

      <Stack spacing={4} sx={{ maxWidth: 400, mx: 'auto' }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', fontWeight: 500 }}>
            What best describes your role?
          </Typography>
          <RHFSelect name="role" native fullWidth size="small">
            <option value="">Select your role</option>
            {ROLE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </RHFSelect>
          
          {selectedRole === 'Other' && (
            <Box sx={{ mt: 2 }}>
              <RHFTextField 
                name="roleOther" 
                placeholder="Please specify your role"
                size="small"
                fullWidth
              />
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 2, textAlign: 'left', fontWeight: 500 }}>
            What type of business do you want to use FlowBookings for?
          </Typography>
          <RHFSelect name="businessType" native fullWidth size="small">
            <option value="">Select business type</option>
            {BUSINESS_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </RHFSelect>
          
          {selectedBusinessType === 'Other' && (
            <Box sx={{ mt: 2 }}>
              <RHFTextField 
                name="businessTypeOther" 
                placeholder="Please specify your business type"
                size="small"
                fullWidth
              />
            </Box>
          )}
        </Box>

        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          onClick={onNext}
          loading={isLoading}
          sx={{ 
            mt: 4,
            py: 1.5,
            fontSize: '16px',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          {isLoading ? 'Saving...' : 'Next'}
        </LoadingButton>
      </Stack>
    </Box>
  );
}

function AvailabilityStep({ availability, setAvailability, onNext, onBack, isLoading }) {
  
  return (
    <Box sx={{ py: 2 }}>
      
      <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
        Configure your availability
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
        Define the working hours when you're available. You can change this later on in the settings.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Your Timezone:
        </Typography>
        <RHFSelect name="config.timezone" native fullWidth size="small">
          {timezones.map(({ tzCode, label }) => (
            <option key={tzCode} value={tzCode}>
              {label}
            </option>
          ))}
        </RHFSelect>
      </Box>

      <WorkingHoursWithCopy
        availability={availability}
        setAvailability={setAvailability}
      />

      <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ minWidth: 120 }}
        >
          Back
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onNext}
          loading={isLoading}
          sx={{ 
            minWidth: 120
          }}
        >
          {isLoading ? 'Saving...' : 'Next'}
        </LoadingButton>
      </Stack>
    </Box>
  );
}
